import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import PageActions from 'app/store/page/page.actions';
// Selectors
import { selectFilter } from 'app/store/page/page.selectors';
import { selectNewDesignEnabled } from 'app/store/pagesToolbar/pagesToolbar.selectors';
// Mui
import { Box, Toolbar, Chip } from '@mui/material';
// Components
import { Button } from 'app/components/Mui';
import ColorPalette from 'app/components/ColorPalette';
// Constants
import { NO_TAG_LABEL } from 'app/App.constants';
// Utils
import { camelCaseToNormalText } from 'app/utilities/Utilities';

const FiltersResultsContainer = () => {
  const { t } = useTranslation();

  const { pathname } = useLocation<{ pathname: string }>();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const newDesignEnabled = useAppSelector(selectNewDesignEnabled);
  const filters = useAppSelector(selectFilter);

  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([_, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'boolean') return value === true;
      if (typeof value === 'string') return value !== 'all' && value !== '';
      return false;
    })
  }, [filters]);

  const isWorkspacePath = useMemo(() => {
    return pathname.includes('workspace');
  }, [pathname]);

  const clearFilterValue = (
    value: typeof filters[keyof typeof filters],
    idx?: number
  ) => {
    if (Array.isArray(value)) {
      // Remove specific item by index
      if ( typeof idx !== 'undefined' ){
        const updated = value.filter((_, i) => i !== idx);
        return updated.length > 0 ? updated : [];
      }
      // Clear full array
      return [];
    }
    if ( typeof value === 'boolean' ) return false;
    if ( typeof value === 'string' ) return 'all'; // Or '' if more appropriate
    return undefined;
  };

  const handleClearFilter = (filterKey:keyof typeof filters, idx?:number) => () => {
    const value = filters[filterKey];
    const updatedValue = clearFilterValue(value, idx);
    dispatch(PageActions.setFilter(filterKey as string, updatedValue));
  }

  const handleResetFilter = () => {
    dispatch(PageActions.setInitialField('filter'));
  }

  // Render
  const renderTextChip = (filterKey:keyof typeof filters, label:string, idx?:number) => (
    <Chip
      key={label}
      label={camelCaseToNormalText(`${label}`)}
      onDelete={handleClearFilter(filterKey, idx)}
      color="primary"
      variant="outlined"
    />
  );

  // Tags
  const renderTags = () => {
    if ( !filters.tags.length ) return null;
    return filters.tags.map((tag:string, idx:number) => {
      const label = !tag ? NO_TAG_LABEL : tag;
      return renderTextChip('tags', label, idx);
    })
  }

  // Labels
  const renderLabels = () => {
    if ( !filters.labels.length ) return null;
    return filters.labels.map((label:string, idx:number) => renderTextChip('labels', label, idx))
  }

  // Types
  const renderTypes = () => {
    if ( !filters.types.length ) return null;
    return filters.types.map((type:string, idx:number) => renderTextChip('types', type, idx))
  }

  // Authors
  const renderAuthors = () => {
    if ( !filters.authors.length ) return null;
    return filters.authors.map((type:string, idx:number) => renderTextChip('authors', type, idx))
  }

  // Colors
  const renderColors = () => {
    if ( !filters.colors.length ) return null;
    const key = 'filterResults-colors';
    return (
      <ColorPalette key={key} initialColors={filters.colors} activeColors={filters.colors}>
        {(color, idx) => (
          <ColorPalette.Item
            key={`${key}-${color}-${idx}`}
            color={color}
            onClick={handleClearFilter('colors', idx)}
          />
        )}
      </ColorPalette>
    )
  }

  // AdditionalBatchesOnly
  const renderAdditionalBatchesOnly = () => {
    if ( !filters.additionalBatchesOnly ) return null;
    return renderTextChip('additionalBatchesOnly', 'Additional batches only');
  }

  // HaveDuplicateInWorkspace
  const renderHaveDuplicateInWorkspace = () => {
    if ( !filters.haveDuplicateInWorkspace ) return null;
    const label = isWorkspacePath ? t('pages.workspace.duplicatesOnly') : t('pages.files.haveDuplicateInWorkspace');
    return renderTextChip('haveDuplicateInWorkspace', label);
  }

  // PagesInWorkspace
  const renderPagesInWorkspace = () => {
    if ( filters.pagesInWorkspace === 'all' ) return null;
    const label = filters.pagesInWorkspace === 'true' ? 'In workspace' : 'Not in workspace';
    return renderTextChip('pagesInWorkspace', label);
  }

  if ( !newDesignEnabled ) return null;
  if ( !hasActiveFilters ) return <Box sx={{ pt: 4 }} />
  return (
    <Toolbar
      sx={{
        height: 'initial',
        minHeight: 'initial',
        flexWrap: 'wrap',
        alignItems: 'center',
        py: 2,
        gap: 2,
        mb: 4
      }}
    >
      {renderTags()}
      {renderTypes()}
      {renderLabels()}
      {renderAuthors()}
      {renderPagesInWorkspace()}
      {renderAdditionalBatchesOnly()}
      {renderHaveDuplicateInWorkspace()}
      {renderColors()}
      <Button
        sx={{ textTransform: 'capitalize' }}
        name="Clear filters"
        onClick={handleResetFilter}
      >{t('buttons.clearAllFilters')}</Button>
    </Toolbar>
  )
}

export default FiltersResultsContainer;
